'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Calendar, User, Tag, Link as LinkIcon } from 'lucide-react';
import BlogService from '@/app/services/blog';
import ArticleSkeleton from '@/app/components/skeleton/ArticleSkeleton';
import ArticleError from '@/app/components/error/ArticleError';
import { useTranslations } from 'next-intl';


export default function PageContent({ params }: { params: any }) {
  const t = useTranslations('News');
    const [post, setPost] = useState<any>(null)
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    async function getBlogDetails() {
        setIsLoading(true)
        try {
            const result: any = await BlogService.getDetails(params?.slug);
            if (result?.data?.success) {
                if(result?.data?.data?.length > 0){
                    setPost(result?.data?.data[0])
                    setError(null)
                } else {
                    setPost(null)
                    setError(t('article_not_found'))
                }
            } else {
                setError(result?.data?.message ?? t('article_not_found'))
            }
        } catch (error: any) {
            setError(error?.response?.data?.message ?? t('article_not_found'))
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { getBlogDetails() }, [])

    const getCategoryName = (cat: any) => {
        const langKey = `name_${post.language}` as keyof typeof cat;
        return cat[langKey] || cat.name_en;
    };

    return (
        <>
            {isLoading ? <ArticleSkeleton /> : (
                <>
                    {error ? (
                        <>
                            <ArticleError errorMessage={error} displayButton={true}/>
                        </>
                    ) : (
                        <article className="min-h-screen bg-white">
                            {/* 1. Header & Metadata */}
                            <header className="container mx-auto px-6 pt-16 pb-8 max-w-4xl">
                                <div className="flex gap-2 mb-6">
                                    {post?.categories.map((cat: any) => (
                                        <span key={cat.id} className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                            {getCategoryName(cat)}
                                        </span>
                                    ))}
                                </div>

                                <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-6">
                                    {post?.title}
                                </h1>

                                <p className="text-xl text-gray-500 mb-8 italic">
                                    {post?.description}
                                </p>

                                <div className="flex flex-wrap items-center gap-6 text-gray-400 border-b border-gray-100 pb-8">
                                    <div className="flex items-center gap-2">
                                        <User size={18} />
                                        <span className="text-sm font-medium">{post?.author}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={18} />
                                        <span className="text-sm">{post?.created_at}</span>
                                    </div>
                                </div>
                            </header>

                            {/* 2. Featured Image */}
                            <div className="container mx-auto px-6 mb-16 max-w-5xl">
                                <div className="aspect-video w-full overflow-hidden rounded-3xl shadow-2xl bg-gray-100">
                                    <img
                                        src={post?.cover_image.url}
                                        alt={post?.alt}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>

                            {/* 3. Main Content Section */}
                            <div className="container mx-auto px-6 max-w-5xl">
                                {/* Render HTML Content */}
                                <div
                                    className="prose prose-lg prose-violet max-w-none 
          prose-headings:font-bold prose-p:text-gray-700 prose-strong:text-violet-600"
                                    style={{ 
                                        whiteSpace: "normal", 
                                        wordBreak: "normal", 
                                        overflowWrap: "anywhere" 
                                    }}
                                    dangerouslySetInnerHTML={{ 
                                    __html: post?.content
                                        ?.replaceAll('&nbsp;', ' ')
                                        ?.replaceAll('<p></p>', '<p><br /></p>')
                                    }}
                                />

                                {/* 4. Tags & External Links */}
                                <div className="mt-16 pt-8 border-t border-gray-100">
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        {post?.tags.map((tag: string, i: number) => (
                                            <div key={i} className="flex items-center gap-1 text-gray-500 bg-gray-100 px-3 py-1 rounded-md text-sm">
                                                <Tag size={14} />
                                                {tag}
                                            </div>
                                        ))}
                                    </div>

                                    {post?.links.length > 0 && (
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-bold flex items-center gap-2">
                                                <LinkIcon size={20} className="text-violet-600" />
                                                {t('related_resources')}
                                            </h3>
                                            {post?.links.map((item: any, i: number) => (
                                                <a
                                                    key={i}
                                                    href={item.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="block p-4 border border-gray-200 rounded-xl hover:border-violet-400 hover:bg-violet-50 transition-all group"
                                                >
                                                    <span className="font-semibold group-hover:text-violet-700">{item.title}</span>
                                                    <span className="block text-sm text-gray-400">{item.link}</span>
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <footer className="container mx-auto px-6 py-20 text-center">
                                <Link href="/blog" className="text-violet-600 font-bold hover:underline">
                                    ← {t('back_all_articles')}
                                </Link>
                            </footer>
                        </article>
                    )}
                </>
            )}
        </>
    );
}